import { useRef, useState } from 'react';
import PageShell from '@components/pages/PageShell';
import { ResourceHero, GalleryGrid, Pagination, SectionLabel, EmptyList } from '@components/pages/ResourceSections';
import Newsletter from '@components/landing/Newsletter';
import { GALLERY } from '@/data/resources';
import { usePagedList } from '@/lib/usePagedList';
import Seo from '@components/Seo';

/** Enough for both tabs to fill a screen before the pager is needed. */
const PER_PAGE = 24;

export default function Gallery() {
    const [query, setQuery] = useState('');
    const grid = useRef<HTMLElement>(null);
    const { page, setPage, pages, matches, pageItems } = usePagedList(
        GALLERY,
        PER_PAGE,
        query,
        (g) => `${g.title} ${g.caption ?? ''}`,
    );
    const searching = query.trim().length > 0;

    const goTo = (next: number) => {
        setPage(next);
        grid.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    };

    return (
        <PageShell>
            <Seo
                title="Gallery"
                description="Clinics, launches, training weeks and the courtrooms in between — the work as it actually looks, in film and photographs."
            />
            <ResourceHero
                titleLead="Our"
                titleBold="Gallery"
                intro="Clinics, launches, training weeks and the courtrooms in between — the work as it actually looks, in film and photographs."
                query={query}
                onSearch={setQuery}
                searchPlaceholder="Search the gallery"
            />

            <section ref={grid} className="scroll-mt-24 bg-white pb-14 pt-14 lg:pb-20 lg:pt-20" aria-live="polite">
                <div className="mx-auto max-w-[1640px] px-5 lg:px-[140px]">
                    <div className="flex flex-wrap items-baseline justify-between gap-3">
                        <SectionLabel>{searching ? 'Search results' : 'All Media'}</SectionLabel>
                        <p className="text-[13px] text-[#5e5e5e]">
                            {searching ? `${matches.length} for “${query.trim()}”` : `${GALLERY.length} items`}
                        </p>
                    </div>
                    {pageItems.length > 0 ? (
                        <GalleryGrid items={pageItems} />
                    ) : (
                        <EmptyList query={query} whenSearching={`Nothing matches “${query.trim()}”.`} />
                    )}
                    <Pagination page={page} pages={pages} onChange={goTo} />
                </div>
            </section>

            <Newsletter />
        </PageShell>
    );
}
